import { supabase } from "@/lib/supabase";
import { getCurrentOrganizationId } from "@/helpers/auth.helpers";
import type { ContractorInterest } from "@/data/contractor/contractorInterest";
import type { MarketplaceOffer } from "@/data/contractor/marketplaceOffer";
import { SystemUpdateService } from "@/services/system-update.service";

function isUuid(value: string | null | undefined): value is string {
  return (
    typeof value === "string" &&
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value)
  );
}

function resolveOrgFromMetadata(metadata: unknown): string | null {
  if (!metadata || typeof metadata !== "object") return null;
  const m = metadata as Record<string, unknown>;
  const candidates = [
    m.organization_id,
    m.organizationId,
    m.tenant_id,
    m.tenantId,
  ];
  for (const candidate of candidates) {
    if (typeof candidate === "string" && isUuid(candidate)) return candidate;
  }
  return null;
}

export const MarketplaceService = {
  async resolveOfferOrganizationId(input: {
    jobId: string;
    contractorId?: string | null;
  }): Promise<string | null> {
    let orgId: string | null = null;
    try {
      const resolved = await getCurrentOrganizationId();
      orgId = isUuid(resolved) ? resolved : null;
    } catch {
      orgId = null;
    }

    // Fallback: derive org from the owning job when auth-context org is unavailable.
    if (!orgId) {
      const jobQuery = supabase
        .from("jobs")
        .select("organization_id, metadata")
        .limit(1);
      const { data: jobRow } = isUuid(input.jobId)
        ? await jobQuery.eq("id", input.jobId).maybeSingle()
        : await jobQuery.eq("job_number", input.jobId).maybeSingle();
      const row = jobRow as { organization_id?: string | null; metadata?: unknown } | null;
      const derivedCol = row?.organization_id ?? null;
      orgId = isUuid(derivedCol) ? derivedCol : resolveOrgFromMetadata(row?.metadata);
    }

    // Fallback: contractor tenant
    if (!orgId && isUuid(input.contractorId)) {
      const { data: contractorRow } = await supabase
        .from("contractors")
        .select("organization_id, metadata")
        .eq("id", input.contractorId)
        .maybeSingle();
      const row = contractorRow as { organization_id?: string | null; metadata?: unknown } | null;
      const derivedCol = row?.organization_id ?? null;
      orgId = isUuid(derivedCol) ? derivedCol : resolveOrgFromMetadata(row?.metadata);
    }

    // Final fallback: company app settings tenant
    if (!orgId) {
      const { data: settingsRow } = await supabase
        .from("app_settings")
        .select("organization_id")
        .eq("setting_type", "company")
        .maybeSingle();
      const derived =
        (settingsRow as { organization_id?: string | null } | null)?.organization_id ?? null;
      orgId = isUuid(derived) ? derived : null;
    }

    return orgId;
  },

  async upsertOffer(offer: MarketplaceOffer): Promise<void> {
    const orgId = await this.resolveOfferOrganizationId({
      jobId: offer.jobId,
      contractorId: offer.contractorId,
    });
    if (!orgId) {
      throw new Error(
        "ORG_RESOLUTION_FAILED: unable to resolve organization_id for marketplace offer",
      );
    }

    const payload: Record<string, unknown> = {
      id: offer.id,
      job_id: offer.jobId,
      contractor_id: offer.contractorId,
      status: offer.status,
      sent_at: offer.sentAt,
      sent_by: offer.sentBy,
      expires_at: offer.expiresAt,
      payout_amount_snapshot: offer.payoutAmountSnapshot ?? null,
      responded_at: offer.respondedAt ?? null,
      response_reason: offer.responseReason ?? null,
      response_note: offer.responseNote ?? null,
      audit: offer.audit ?? [],
    };
    payload.organization_id = orgId;

    const { error } = await supabase.from("marketplace_offers").upsert(payload, {
      onConflict: "id",
    });
    if (error) throw error;
    // Post a system update when a new offer is sent (status === 'pending' and
    // audit has exactly one entry — the offer_created audit entry).
    if (offer.status === 'pending' && offer.audit.length <= 1) {
      const jobLabel = offer.jobId;
      void SystemUpdateService.post({
        domain:   'offers',
        event:    'offer_created',
        body:     `System: Offer Sent to Installer — ${jobLabel}`,
        metadata: {
          title:  'Offer Sent to Installer',
          detail: `${jobLabel}`,
          action: { label: 'View Offers', path: '/dispatch-intelligence' },
        },
      })
    }
  },

  async closeInterestAsOfferSent(interestId: string): Promise<void> {
    const { error } = await supabase
      .from("contractor_interests")
      .update({
        status: "offer_sent",
        close_reason: "offer_sent",
        closed_at: new Date().toISOString(),
      })
      .eq("id", interestId);
    if (error) throw error;
  },

  async insertInterest(interest: ContractorInterest): Promise<void> {
    let orgId: string | null = null;
    try {
      const resolved = await getCurrentOrganizationId();
      orgId = isUuid(resolved) ? resolved : null;
    } catch {
      orgId = null;
    }

    const payload: Record<string, unknown> = {
      id: interest.id,
      job_id: interest.jobId,
      contractor_id: interest.contractorId,
      status: interest.status,
      submitted_at: interest.submittedAt,
      closed_at: null,
      close_reason: null,
    };
    if (orgId) {
      payload.organization_id = orgId;
    }

    const { error } = await supabase.from("contractor_interests").insert(payload);
    if (error) throw error;
  },

  async dismissInterest(interestId: string): Promise<void> {
    const { error } = await supabase
      .from("contractor_interests")
      .update({
        status: "dismissed",
        close_reason: "dispatcher_dismissed",
        closed_at: new Date().toISOString(),
      })
      .eq("id", interestId);
    if (error) throw error;
  },

  async listOffers(): Promise<MarketplaceOffer[]> {
    const { data, error } = await supabase
      .from("marketplace_offers")
      .select(
        "id, job_id, contractor_id, status, sent_at, sent_by, expires_at, " +
        "payout_amount_snapshot, responded_at, response_reason, response_note, audit",
      )
      .order("sent_at", { ascending: false });

    if (error || !data) {
      console.warn("[MarketplaceService.listOffers] error:", error);
      return [];
    }

    return (data as Record<string, unknown>[]).map((row) => ({
      id: row.id as string,
      jobId: row.job_id as string,
      contractorId: row.contractor_id as string,
      status: row.status as MarketplaceOffer["status"],
      sentAt: row.sent_at as string,
      sentBy: row.sent_by as string,
      expiresAt: row.expires_at as string,
      payoutAmountSnapshot:
        row.payout_amount_snapshot != null
          ? Number(row.payout_amount_snapshot)
          : undefined,
      respondedAt: (row.responded_at as string | null) ?? undefined,
      responseReason: (row.response_reason as MarketplaceOffer["responseReason"] | null) ?? undefined,
      responseNote: (row.response_note as string | null) ?? undefined,
      audit: Array.isArray(row.audit) ? (row.audit as MarketplaceOffer["audit"]) : [],
    }));
  },

  async getOffersByJobId(jobId: string): Promise<MarketplaceOffer[]> {
    const { data, error } = await supabase
      .from("marketplace_offers")
      .select(
        "id, job_id, contractor_id, status, sent_at, sent_by, expires_at, " +
        "payout_amount_snapshot, responded_at, response_reason, response_note, audit",
      )
      .eq("job_id", jobId)
      .order("sent_at", { ascending: true });

    if (error || !data) {
      console.warn("[MarketplaceService.getOffersByJobId] error:", error);
      return [];
    }

    return (data as Record<string, unknown>[]).map((row) => ({
      id: row.id as string,
      jobId: row.job_id as string,
      contractorId: row.contractor_id as string,
      status: row.status as MarketplaceOffer["status"],
      sentAt: row.sent_at as string,
      sentBy: row.sent_by as string,
      expiresAt: row.expires_at as string,
      payoutAmountSnapshot:
        row.payout_amount_snapshot != null
          ? Number(row.payout_amount_snapshot)
          : undefined,
      respondedAt: (row.responded_at as string | null) ?? undefined,
      responseReason: (row.response_reason as MarketplaceOffer["responseReason"] | null) ?? undefined,
      responseNote: (row.response_note as string | null) ?? undefined,
      audit: Array.isArray(row.audit) ? (row.audit as MarketplaceOffer["audit"]) : [],
    }));
  },
};
